/**
 * Embedded Flow Editor
 * A React Flow editor that syncs with the DOM (#ListaAccionesOrden)
 * Used inline in the form editor instead of a modal
 *
 * Features:
 * - Drag & drop to reorder nodes
 * - Groups with children inside (parent nodes)
 * - Alternatives with handles per option
 */

import { useCallback, useEffect, useState, useRef, useMemo } from 'react';
import {
  ReactFlow,
  Background,
  MiniMap,
  useNodesState,
  useEdgesState,
  useReactFlow,
  ReactFlowProvider,
  BackgroundVariant,
} from '@xyflow/react';
import type { Node, Edge, NodeMouseHandler } from '@xyflow/react';
import '@xyflow/react/dist/style.css';

import type { ActionNodeData } from '../types';
import { buildFlowFromDOM, applySimpleLayout } from '../services/domFlowBuilder';
import ActionNodeComponent from './ActionNode';
import GroupNodeComponent from './GroupNode';
import AddButtonEdge from './AddButtonEdge';
import ReorderList from './ReorderList';

// Custom node types
const nodeTypes = {
  actionNode: ActionNodeComponent,
  groupNode: GroupNodeComponent,
};

// Custom edge types
const edgeTypes = {
  addButton: AddButtonEdge,
};

interface EmbeddedFlowEditorProps {
  onNodeClick?: (codigoUnico: string) => void;
  selectedCodigoUnico?: string | null;
}

// Inner component with ReactFlow hooks
function EmbeddedFlowEditorInner({
  onNodeClick,
  selectedCodigoUnico,
}: EmbeddedFlowEditorProps) {
  const flowRef = useRef<HTMLDivElement>(null);
  const [nodes, setNodes, onNodesChange] = useNodesState<Node<ActionNodeData>>([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState<Edge>([]);
  const [orientation] = useState<'LR' | 'TB'>('TB');
  const [isLoading, setIsLoading] = useState(true);
  const [isEmpty, setIsEmpty] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [isReorderMode, setIsReorderMode] = useState(false);

  const { fitView, zoomIn, zoomOut, getNodes } = useReactFlow();

  // Build flow from DOM
  // NOTE: Don't include selectedCodigoUnico in dependencies - selection is handled separately
  const rebuildFlow = useCallback((fitAfterBuild = false) => {
    const { nodes: flowNodes, edges: flowEdges } = buildFlowFromDOM(null);

    if (flowNodes.length === 0) { // No actions
      setIsEmpty(true);
      setNodes([]);
      setEdges([]);
      setIsLoading(false);
      return;
    }

    setIsEmpty(false);

    // Apply layout
    const positionedNodes = applySimpleLayout(flowNodes, flowEdges, orientation);

    setNodes(positionedNodes as Node<ActionNodeData>[]);
    setEdges(flowEdges as Edge[]);
    setIsLoading(false);

    if (fitAfterBuild) {
      setTimeout(() => {
        fitView({ padding: 0.15, duration: 300 });
      }, 50);
    }
  }, [orientation, setNodes, setEdges, fitView]);

  // Prepare items for reorder list
  const reorderItems = useMemo(() => {
    return nodes
      .filter(node => node.id !== 'INICIO' && node.id !== 'FIN' && !node.parentId)
      .map(node => {
        const data = node.data as ActionNodeData;
        const children = nodes.filter(n => n.parentId === node.id);
        return {
          codigoUnico: data?.codigoUnico || node.id,
          label: data?.label || '',
          orden: data?.orden || 0,
          tipo: data?.tipo || 'action',
          tieneIA: data?.tieneIA,
          isGroup: data?.tipo === 'grupo',
          childCount: children.length,
        };
      })
      .sort((a, b) => a.orden - b.orden);
  }, [nodes]);

  // Handle reorder from ReorderList
  const handleReorder = useCallback((newOrder: string[]) => {
    console.log('[EmbeddedFlow] New order from ReorderList:', newOrder);
    window.dispatchEvent(new CustomEvent('flow-reorder', {
      detail: { newOrder },
    }));

    // Rebuild after DOM is updated
    setTimeout(() => {
      rebuildFlow(false);
    }, 300);
  }, [rebuildFlow]);

  // Exit reorder mode
  const handleExitReorder = useCallback(() => {
    setIsReorderMode(false);
    // Fit view after exiting
    setTimeout(() => {
      fitView({ padding: 0.15, duration: 300 });
    }, 100);
  }, [fitView]);

  // Initial load
  useEffect(() => {
    const timer = setTimeout(() => {
      rebuildFlow(true);
    }, 100);

    return () => clearTimeout(timer);
  }, [rebuildFlow]);

  // Listen for DOM changes via CustomEvent
  useEffect(() => {
    const handleAccionesUpdated = () => {
      if (!isDragging) {
        rebuildFlow(false);
      }
    };

    const handleFlowAddAction = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      console.log('[EmbeddedFlow] Add action requested:', detail);

      window.dispatchEvent(new CustomEvent('show-add-action-menu', {
        detail: {
          sourceId: detail.sourceId,
          targetId: detail.targetId,
        },
      }));
    };

    const handleLayoutChanged = () => {
      setTimeout(() => {
        fitView({ padding: 0.15, duration: 300 });
      }, 50);
    };

    window.addEventListener('acciones-updated', handleAccionesUpdated);
    window.addEventListener('flow-add-action', handleFlowAddAction);
    window.addEventListener('flow-layout-changed', handleLayoutChanged);

    return () => {
      window.removeEventListener('acciones-updated', handleAccionesUpdated);
      window.removeEventListener('flow-add-action', handleFlowAddAction);
      window.removeEventListener('flow-layout-changed', handleLayoutChanged);
    };
  }, [rebuildFlow, fitView, isDragging]);

  // Update selection state without rebuilding (prevents camera jump)
  useEffect(() => {
    if (!isLoading && nodes.length > 0) {
      setNodes(currentNodes =>
        currentNodes.map(node => ({
          ...node,
          data: {
            ...node.data,
            isSelected: node.id === selectedCodigoUnico,
          },
        }))
      );
    }
  }, [selectedCodigoUnico, isLoading, setNodes, nodes.length]);

  // Handle node click
  const handleNodeClick: NodeMouseHandler = useCallback((_, node) => {
    const data = node.data as ActionNodeData | undefined;
    const codigoUnico = data?.codigoUnico || node.id;
    if (codigoUnico && codigoUnico !== 'INICIO' && codigoUnico !== 'FIN') {
      onNodeClick?.(codigoUnico);
    }
  }, [onNodeClick]);

  // Handle pane click (deselect)
  const handlePaneClick = useCallback(() => {
    window.dispatchEvent(new CustomEvent('flow-deselect', { detail: {} }));
  }, []);

  // Handle node drag start
  const handleNodeDragStart = useCallback(() => {
    setIsDragging(true);
  }, []);

  // Handle node drag stop - reorder based on position
  const handleNodeDragStop = useCallback((_: React.MouseEvent, node: Node<ActionNodeData>) => {
    setIsDragging(false);

    const data = node.data as ActionNodeData | undefined;
    const codigoUnico = data?.codigoUnico || node.id;

    // Skip special nodes
    if (codigoUnico === 'INICIO' || codigoUnico === 'FIN') {
      rebuildFlow(false); // Reset position
      return;
    }

    // Get all nodes sorted by position
    const allNodes = getNodes();
    const regularNodes = allNodes.filter(n =>
      n.id !== 'INICIO' && n.id !== 'FIN' && !n.parentId
    );

    // Sort by Y position (TB layout) or X position (LR layout)
    const sortedNodes = [...regularNodes].sort((a, b) => {
      if (orientation === 'TB') {
        return a.position.y - b.position.y;
      } else {
        return a.position.x - b.position.x;
      }
    });

    // Get new order
    const newOrder = sortedNodes.map(n => (n.data as ActionNodeData)?.codigoUnico || n.id);

    console.log('[EmbeddedFlow] New order after drag:', newOrder);

    // Dispatch reorder event
    window.dispatchEvent(new CustomEvent('flow-reorder', {
      detail: { newOrder },
    }));

    // Rebuild after DOM is updated
    setTimeout(() => {
      rebuildFlow(false);
    }, 300);
  }, [getNodes, orientation, rebuildFlow]);


  // Loading state
  if (isLoading) {
    return (
      <div className="embedded-flow-loading">
        <div className="loading-spinner" />
        <p>Cargando flujo...</p>
      </div>
    );
  }

  // Empty state
  if (isEmpty) {
    return (
      <div className="embedded-flow-empty">
        <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="1.5">
          <path d="M9 17H5a2 2 0 0 0-2 2v2m16-2v2a2 2 0 0 1-2 2h-4m-4-8h4m-4 0V5a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v4m-8 0H5a2 2 0 0 0-2 2v4" />
        </svg>
        <p>Agrega acciones para ver el flujo</p>
      </div>
    );
  }

  // Reorder mode - show horizontal list
  if (isReorderMode) {
    return (
      <div ref={flowRef} className="embedded-flow-container reorder-mode">
        <ReorderList
          items={reorderItems}
          onReorder={handleReorder}
          onExit={handleExitReorder}
        />
      </div>
    );
  }

  return (
    <div ref={flowRef} className={`embedded-flow-container ${isReorderMode ? 'reorder-mode' : ''}`}>
      {/* Compact toolbar */}
      <div className="embedded-flow-toolbar">
        <button
          onClick={() => fitView({ padding: 0.15, duration: 300 })}
          title="Ajustar vista"
          className="toolbar-btn"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M15 3h6v6M9 21H3v-6M21 3l-7 7M3 21l7-7" />
          </svg>
        </button>
        <button
          onClick={() => zoomIn({ duration: 200 })}
          title="Acercar"
          className="toolbar-btn"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="11" cy="11" r="8" />
            <path d="M21 21l-4.35-4.35M11 8v6M8 11h6" />
          </svg>
        </button>
        <button
          onClick={() => zoomOut({ duration: 200 })}
          title="Alejar"
          className="toolbar-btn"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="11" cy="11" r="8" />
            <path d="M21 21l-4.35-4.35M8 11h6" />
          </svg>
        </button>
        <div className="toolbar-separator" />
        <button
          onClick={() => setIsReorderMode(!isReorderMode)}
          title={isReorderMode ? 'Salir de modo reordenar' : 'Entrar en modo reordenar'}
          className={`toolbar-btn ${isReorderMode ? 'toolbar-btn-active' : ''}`}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M3 6h18M3 12h18M3 18h18" />
            {isReorderMode && <path d="M7 3v18M17 3v18" strokeOpacity="0.5" />}
          </svg>
        </button>
        {isReorderMode && (
          <span className="toolbar-hint toolbar-hint-active">
            Arrastra para reordenar
          </span>
        )}
      </div>

      {/* React Flow */}
      <ReactFlow
        nodes={nodes}
        edges={isReorderMode ? [] : edges}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        onNodeClick={handleNodeClick}
        onPaneClick={handlePaneClick}
        onNodeDragStart={isReorderMode ? handleNodeDragStart : undefined}
        onNodeDragStop={isReorderMode ? handleNodeDragStop : undefined}
        nodeTypes={nodeTypes}
        edgeTypes={edgeTypes}
        minZoom={0.2}
        maxZoom={1.5}
        nodesDraggable={isReorderMode}
        nodesConnectable={false}
        elementsSelectable={true}
        panOnDrag={true}
        zoomOnScroll={true}
        selectNodesOnDrag={false}
        defaultEdgeOptions={{
          type: 'addButton',
          animated: false,
        }}
        proOptions={{ hideAttribution: true }}
      >
        <Background
          variant={BackgroundVariant.Dots}
          gap={20}
          size={1}
          color="#e5e7eb"
        />

        <MiniMap
          nodeStrokeWidth={2}
          zoomable
          pannable
          position="bottom-right"
          style={{
            backgroundColor: '#fff',
            border: '1px solid #e5e7eb',
            borderRadius: 4,
            width: 120,
            height: 80,
          }}
          nodeColor={(node) => {
            const data = node.data as ActionNodeData;
            if (data?.isSelected) return '#10b981';
            if (data?.tieneIA) return '#10b981';
            if (data?.tipo === 'grupo') return '#f59e0b';
            if (data?.finaliza) return '#ef4444';
            return '#94a3b8';
          }}
        />
      </ReactFlow>
    </div>
  );
}

// Wrapper with provider
export default function EmbeddedFlowEditor(props: EmbeddedFlowEditorProps) {
  return (
    <ReactFlowProvider>
      <EmbeddedFlowEditorInner {...props} />
    </ReactFlowProvider>
  );
}

// CSS styles
const styles = `
.embedded-flow-container {
  width: 100%;
  height: 100%;
  position: relative;
  background: #fafafa;
  border-radius: 6px;
  overflow: hidden;
}

.embedded-flow-loading,
.embedded-flow-empty {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: #fafafa;
  color: #6b7280;
  font-size: 13px;
}

.embedded-flow-loading .loading-spinner {
  width: 24px;
  height: 24px;
  border: 2px solid #e5e7eb;
  border-top-color: #6b7280;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
  margin-bottom: 8px;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.embedded-flow-toolbar {
  position: absolute;
  top: 8px;
  left: 8px;
  z-index: 10;
  display: flex;
  align-items: center;
  gap: 2px;
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  padding: 4px;
  box-shadow: 0 1px 3px rgba(0,0,0,0.1);
}

.toolbar-btn {
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  color: #6b7280;
  transition: background 0.15s, color 0.15s;
}

.toolbar-btn:hover {
  background: #f3f4f6;
  color: #374151;
}

.toolbar-separator {
  width: 1px;
  height: 20px;
  background: #e5e7eb;
  margin: 0 4px;
}

.toolbar-hint {
  font-size: 11px;
  color: #9ca3af;
  padding: 0 6px;
}

.toolbar-hint-active {
  color: #f59e0b;
  font-weight: 500;
}

.toolbar-btn-active {
  background: #fef3c7 !important;
  color: #d97706 !important;
  border: 1px solid #fcd34d;
}

/* React Flow overrides for embedded */
.embedded-flow-container .react-flow__node {
  cursor: pointer;
}

.embedded-flow-container.reorder-mode .react-flow__node {
  cursor: grab;
}

.embedded-flow-container.reorder-mode .react-flow__node:active {
  cursor: grabbing;
}

.embedded-flow-container .react-flow__node.dragging {
  z-index: 1000 !important;
}

.embedded-flow-container .react-flow__controls {
  display: none;
}

/* ===== ReorderList Styles ===== */
.reorder-list-container {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  background: #f8fafc;
  overflow: hidden;
}

.reorder-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  background: linear-gradient(135deg, #fef3c7 0%, #fde68a 100%);
  border-bottom: 1px solid #fcd34d;
}

.reorder-header-left {
  display: flex;
  align-items: center;
  gap: 10px;
  color: #92400e;
  font-weight: 600;
  font-size: 14px;
}

.reorder-hint {
  font-weight: 400;
  font-size: 12px;
  color: #a16207;
}

.reorder-exit-btn {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  background: white;
  border: 1px solid #d97706;
  border-radius: 6px;
  color: #d97706;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.15s;
}

.reorder-exit-btn:hover {
  background: #d97706;
  color: white;
}

.reorder-items {
  flex: 1;
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  padding: 16px;
  overflow-y: auto;
  align-content: flex-start;
}

.reorder-item-wrapper {
  display: flex;
  align-items: center;
}

.reorder-drop-indicator {
  width: 4px;
  height: 48px;
  background: #3b82f6;
  border-radius: 2px;
  margin: 0 4px;
  animation: pulse-indicator 0.5s ease-in-out infinite alternate;
}

@keyframes pulse-indicator {
  from { opacity: 0.5; transform: scaleY(0.9); }
  to { opacity: 1; transform: scaleY(1); }
}

.reorder-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 14px;
  background: white;
  border: 1px solid #e5e7eb;
  border-left-width: 4px;
  border-radius: 8px;
  cursor: grab;
  transition: all 0.15s;
  user-select: none;
  min-width: 140px;
  max-width: 280px;
}

.reorder-item:hover {
  box-shadow: 0 4px 12px rgba(0,0,0,0.1);
  transform: translateY(-1px);
}

.reorder-item:active {
  cursor: grabbing;
}

.reorder-item.dragging {
  opacity: 0.4;
  transform: scale(0.95);
  box-shadow: none;
}

.reorder-item-order {
  min-width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 6px;
  color: white;
  font-size: 12px;
  font-weight: 600;
  flex-shrink: 0;
}

.reorder-item-label {
  flex: 1;
  font-size: 13px;
  font-weight: 500;
  color: #374151;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.reorder-item-badge {
  font-size: 9px;
  font-weight: 600;
  text-transform: uppercase;
  padding: 2px 6px;
  border-radius: 4px;
  flex-shrink: 0;
}

.reorder-item-badge.group {
  background: #fef3c7;
  color: #92400e;
}

.reorder-item-badge.ia {
  background: #ecfdf5;
  color: #059669;
}

.reorder-handle {
  color: #9ca3af;
  flex-shrink: 0;
  opacity: 0.5;
  transition: opacity 0.15s;
}

.reorder-item:hover .reorder-handle {
  opacity: 1;
}

.reorder-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 16px;
  background: #f1f5f9;
  border-top: 1px solid #e2e8f0;
  font-size: 12px;
  color: #64748b;
}

.reorder-footer kbd {
  display: inline-block;
  padding: 2px 6px;
  background: white;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  font-family: inherit;
  font-size: 11px;
  box-shadow: 0 1px 2px rgba(0,0,0,0.05);
}
`;

// Inject styles
if (typeof document !== 'undefined') {
  const styleEl = document.createElement('style');
  styleEl.textContent = styles;
  document.head.appendChild(styleEl);
}
